import React, { useEffect, useCallback, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBeveragesV2 } from "../../hooks/useBeveragesV2";
import { useAcid } from "../../hooks/useAcid";

import { SignInDialogUi } from "../../components";
import actionWebInvoke from "../../utils/actionWebInvoke";
import actions from "../../config.json";
import { transformAiBeverageData } from "../../utils/transformAiBeverageData";
import { useCampaignPage } from "../../store";
import styles from "./BeverageLandingPages.module.css";
import classNames from "classnames";
import {
  sendAnalyticsPageViewEvent,
  sendPageViewWhenConsentReady,
} from "../../utils/adobeDataLayerUtils";
import { stripAcidFromSearch } from "../../utils/postLoginRedirect";

export const BeverageV2Save = () => {
  const cid = "beverage";
  const navigate = useNavigate();
  const location = useLocation();
  const { acid } = useAcid();
  const [saveError, setSaveError] = useState(false);
  const { beverages, saveBeveragesToStorage } = useBeveragesV2();
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const { setShowPageLoader } = useCampaignPage();
  // Use a ref-based lock to prevent the mount effect from re-entering save() while the initial request is still in flight.
  const savingRef = useRef(false);

  useEffect(() => {
    saveBeveragesToStorage();
  }, []);

  const onCloseDialog = useCallback(() => {
    setShowSignInDialog(false);
    navigate("/beverage2");
  }, [setShowSignInDialog]);

  const back = () => {
    navigate("/beverage2");
  };

  const onSubmitDialog = useCallback(() => {
    onCloseDialog();
    setShowPageLoader(true);
  }, [onCloseDialog, setShowPageLoader]);

  const save = useCallback(async () => {
    if (!acid) {
      setShowSignInDialog(true);
      sendAnalyticsPageViewEvent(
        "Beverage V2 Modal: Sign In",
        "Passions-FavBevs",
        "beverages2",
      );
      return;
    }

    if (savingRef.current) {
      return; // Prevent double-submission
    }
    savingRef.current = true;

    sendPageViewWhenConsentReady(
      "Beverage V2 Save",
      "Passions-FavBevs",
      "beverages2",
    );

    try {
      // Transform flat AI response to structuredBeverageData format
      const structuredBeverageData = transformAiBeverageData(beverages);

      // Call the save-beverage-preferences action with structured data
      await actionWebInvoke(
        actions["marriott-passions-v3/save-beverage-preferences"],
        {},
        {
          acid,
          structuredBeverageData,
        },
      );

      // If the guest hasn't answered the personalization prompt this session, show it next; otherwise continue to the thank-you page.
      const hasRespondedToConsent =
        sessionStorage.getItem("beverageConsentResponded") === "true";
      navigate(
        hasRespondedToConsent
          ? "/beverage2/thank-you"
          : "/beverage2/opt-confirm",
      );
    } catch (error) {
      setSaveError(true);
      console.error("Error saving beverage preferences:", error);
    } finally {
      savingRef.current = false;
    }
  }, [acid, beverages, navigate]);

  useEffect(() => {
    if (!saveError) {
      save();
    }
  }, [save]);

  return (
    <>
      {showSignInDialog && (
        <SignInDialogUi
          onClose={onCloseDialog}
          onSubmit={onSubmitDialog}
          selectedPassions={[]}
          cid={cid}
          redirectUrl={`/beverage2/save${stripAcidFromSearch(location.search)}`}
        />
      )}
      <main className={classNames(styles.main, styles.saveMain)}>
        {!saveError ? <span className={styles.pageLoader}></span> : null}
        {saveError ? (
          <>
            <span>Cannot save the data. Please try again later</span>
            <button
              onClick={back}
              className={`${styles.submit} custom_click_track`}
              data-custom_click_track_value="Beverage V2 Save|Back|internal"
            >
              Back
            </button>
          </>
        ) : null}
      </main>
    </>
  );
};
